import { createSlice } from '@reduxjs/toolkit'
import BigNumber from 'bignumber.js'
import boostersConfig from '~/config/constants/boosters'
import { BIG_ZERO } from '~/utils/bigNumber'
import { BoostersState, AppThunk } from '~/state/types'
import { getPoolApr } from '~/utils/apr'
import { getBalanceNumber } from '~/utils/formatBalance'
import { toSerializedPoolConfig } from '~/state/pools/helpers'
import { fetchBoostersBlockLimits, fetchBoostersPendingBurnBatchReward, fetchBoostersStakingLimits, fetchBoostersTotalStaking } from './fetchBoosters'
import {
  fetchBoostersAllowance,
  fetchUserBalances,
  fetchUserStakeBalances,
  fetchUserPendingRewards,
  fetchUserTimeToUnlock
} from './fetchBoostersUser'
// import fetchVaultUser from './fetchVaultUser'
import { getTokenPricesFromFarm } from './helpers'

const initPoolConfig = Object.keys(boostersConfig).reduce((accum, key) => {
  /* eslint-disable no-param-reassign */
  accum[key] = boostersConfig[key].map(pool => toSerializedPoolConfig( Number.parseInt(key), pool))
  return accum
}, {} )


const initialState: BoostersState = {
  data: initPoolConfig,
  userDataLoaded: false,
}

// Thunks
export const fetchBoostersPublicDataAsync = (chainId: number, currentBlock: number) => async (dispatch, getState) => {
  const blockLimits = await fetchBoostersBlockLimits(chainId)
  const totalStakings = await fetchBoostersTotalStaking(chainId)

  const pendingBurnBatchRewards = await fetchBoostersPendingBurnBatchReward(chainId)

  const prices = getTokenPricesFromFarm(getState().farms.data[chainId])

  const liveData = boostersConfig[chainId].map( boosterPool => toSerializedPoolConfig(chainId, boosterPool)).map((pool) => {
    const blockLimit = blockLimits.find((entry) => entry.sousId === pool.sousId)
    const totalStaking = totalStakings.find((entry) => entry.sousId === pool.sousId)
    const pendingBurnBatchReward = pendingBurnBatchRewards.find((entry) => entry.sousId === pool.sousId)
    const isPoolEndBlockExceeded = currentBlock > 0 && blockLimit ? currentBlock > Number(blockLimit.endBlock) : false
    const isPoolFinished = pool.isFinished || isPoolEndBlockExceeded

    const stakingTokenAddress = pool.stakingToken.address ? pool.stakingToken.address.toLowerCase() : null
    const stakingTokenPrice = stakingTokenAddress ? prices[stakingTokenAddress] : 0

    const earningTokenAddress = pool.earningToken.address ? pool.earningToken.address.toLowerCase() : null
    const earningTokenPrice = earningTokenAddress ? prices[earningTokenAddress] : 0
    const apr = !isPoolFinished
      ? getPoolApr(
          stakingTokenPrice,
          earningTokenPrice,
          getBalanceNumber(new BigNumber(totalStaking.totalStaked), pool.stakingToken.decimals),
          parseFloat(pool.tokenPerSecond),
        )
      : 0

    return {
      ...blockLimit,
      ...totalStaking,
      stakingTokenPrice,
      earningTokenPrice,
      apr,
      isFinished: isPoolFinished,
      pendingBurnBatchReward: pendingBurnBatchReward.pendingBurnBatchReward
    }
  })

  dispatch(setBoostersPublicData({chainId, poolsData: liveData}))
}

export const fetchBoostersPendingBurnBatchRewardAsync = (chainId: number) => async (dispatch, getState) => {
    const rewardData = await fetchBoostersPendingBurnBatchReward(chainId)
    dispatch(setBoostersPublicData({chainId, poolsData: rewardData}))
}

export const fetchBoostersStakingLimitsAsync = (chainId: number) => async (dispatch, getState) => {
  const poolsWithStakingLimit = getState()
    .boosters.data[chainId].filter(({ stakingLimit }) => stakingLimit !== null && stakingLimit !== undefined)
    .map((pool) => pool.sousId)

  const stakingLimits = await fetchBoostersStakingLimits(chainId, poolsWithStakingLimit)

  const stakingLimitData = boostersConfig[chainId].map((pool) => {
    if (poolsWithStakingLimit.includes(pool.sousId)) {
      return { sousId: pool.sousId }
    }
    const stakingLimit = stakingLimits[pool.sousId] || BIG_ZERO
    return {
      sousId: pool.sousId,
      stakingLimit: stakingLimit.toJSON(),
    }
  })

  dispatch(setBoostersPublicData({chainId, poolsData: stakingLimitData}))
}

export const fetchBoostersUserDataAsync =
  (chainId: number, account: string): AppThunk =>
  async (dispatch) => {
    const allowances = await fetchBoostersAllowance(chainId, account)
    const stakingTokenBalances = await fetchUserBalances(chainId, account)
    const stakedBalances = await fetchUserStakeBalances(chainId, account)
    const pendingRewards = await fetchUserPendingRewards(chainId, account)
    const timeToUnlock = await fetchUserTimeToUnlock(chainId, account)

    const userData = boostersConfig[chainId].map((pool) => ({
      sousId: pool.sousId,
      allowance: allowances[pool.sousId],
      stakingTokenBalance: stakingTokenBalances[pool.sousId],
      stakedBalance: stakedBalances[pool.sousId],
      pendingReward: pendingRewards[pool.sousId],
      timeToUnlock: timeToUnlock[pool.sousId]
    }))

    dispatch(setBoostersUserData({chainId, userData} ))
  }

export const updateUserAllowance =
  (chainId: number, sousId: number, account: string): AppThunk =>
  async (dispatch) => {
    const allowances = await fetchBoostersAllowance(chainId, account)
    dispatch(updateBoostersUserData({ chainId, sousId, field: 'allowance', value: allowances[sousId] }))
  }

export const updateUserBalance =
  (chainId: number, sousId: number, account: string): AppThunk =>
  async (dispatch) => {
    const tokenBalances = await fetchUserBalances(chainId, account)
    dispatch(updateBoostersUserData({ chainId, sousId, field: 'stakingTokenBalance', value: tokenBalances[sousId] }))
  }

export const updateUserStakedBalance =
  (chainId: number, sousId: number, account: string): AppThunk =>
  async (dispatch) => {
    const stakedBalances = await fetchUserStakeBalances(chainId, account)
    dispatch(updateBoostersUserData({ chainId, sousId, field: 'stakedBalance', value: stakedBalances[sousId] }))
  }

export const updateUserPendingReward =
  (chainId: number, sousId: number, account: string): AppThunk =>
  async (dispatch) => {
    const pendingRewards = await fetchUserPendingRewards(chainId, account)
    dispatch(updateBoostersUserData({ chainId, sousId, field: 'pendingReward', value: pendingRewards[sousId] }))
  }



export const BoostersSlice = createSlice({
  name: 'Boosters',
  initialState,
  reducers: {
    setBoostersPublicData: (state, action) => {
      const { chainId, poolsData } = action.payload
      state.data[chainId] = state.data[chainId].map((pool) => {
        const livePoolData = poolsData.find((entry) => entry.sousId === pool.sousId)
        return { ...pool, ...livePoolData }
      })
    },
    setBoostersUserData: (state, action) => {
      const { chainId, userData }= action.payload
      state.data[chainId] = state.data[chainId].map((pool) => {
        const userPoolData = userData.find((entry) => entry.sousId === pool.sousId)
        return { ...pool, userData: userPoolData }
      })
      state.userDataLoaded = true
    },
    updateBoostersUserData: (state, action) => {
      const { chainId, field, value, sousId } = action.payload
      const index = state.data[chainId].findIndex((p) => p.sousId === sousId)

      if (index >= 0) {
        state.data[chainId][index] = { ...state.data[chainId][index], userData: { ...state.data[chainId][index].userData, [field]: value } }
      }
    },
  },
  // extraReducers: (builder) => {

  // },
})

// Actions
export const { setBoostersPublicData, setBoostersUserData, updateBoostersUserData } = BoostersSlice.actions

export default BoostersSlice.reducer
